import React, { useEffect, useCallback, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Building2,
  MapPin,
  Phone,
  FileText,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Search,
  Sparkles,
  Stethoscope,
  Info,
  Link2,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { practitionerService } from '@/services/practitionerService';
import { getDEAValidationError, isValidEmail } from '@/lib/validators';
import { isValidPhoneNumber } from 'react-phone-number-input';
import PhoneInput from '@/components/ui/phone-input';
import { PhoneField } from '@/components/booking/PhoneField';
import PlacesAutocomplete from '@/components/ui/google-places-autocomplete';
import { State } from '@/models/state.model';
import { stateService } from '@/services/stateService';
import { clinicOnboardingService, NpiLookupResult } from '@/services/clinicOnboardingService';
import { ClinicOnboardingData } from '../types';
import { useToast } from '@/hooks/use-toast';
import type { BasicInfoStepHandle } from '../../ClinicOnboarding';

const baseInfoSchema = z.object({
    name: z
      .string()
      .min(1, 'Clinic name is required')
      .max(120, 'Clinic name must be at most 120 characters'),
    npi: z.string().optional().or(z.literal('')).refine(val => { if (!val || val === '') return true; return /^\d{10}$/.test(val); }, { message: 'NPI must be exactly 10 digits' }),
    address_line_1: z
      .string()
      .nonempty('Address is required')
      .min(5, 'Address must be at least 5 characters long')
      .max(100, 'Address must be at most 100 characters long')
      .trim(),
    address_line_2: z
      .string()
      .max(100, 'Address line 2 must be at most 100 characters long')
      .optional()
      .or(z.literal('')),
    city: z.string().nonempty('City is required').min(2, 'Please enter a valid city'),
    state: z.string().nonempty('State is required').min(2, 'Please select a state'),
    postal_code: z
      .string()
      .regex(/^\d{5}(-\d{4})?$/, 'Invalid postal code')
      .nonempty('Postal code is required'),
    primary_phone: z
      .string()
      .min(1, 'Phone number is required')
      .refine(
        val => {
          if (!val) return false;
          try {
            return isValidPhoneNumber(val, 'US');
          } catch {
            return false;
          }
        },
        { message: 'Please enter a valid phone number' }
      ),
    fax: z
      .string()
      .optional()
      .or(z.literal(''))
      .refine(
        val => {
          if (!val) return true;
          try {
            return isValidPhoneNumber(val, 'US');
          } catch {
            return false;
          }
        },
        { message: 'Invalid fax number' }
      ),
    email: z.string().min(1, 'Email is required').email('Please enter a valid email address')
      .refine(isValidEmail, { message: 'Email must include a valid domain extension (e.g., .com, .org)' }),
    description: z
      .string()
      .min(10, 'Description must be at least 10 characters long')
      .max(1000, 'Description cannot exceed 1000 characters')
      .nonempty('Description is required'),
    admin_first_name: z
      .string()
      .min(2, 'First name is required')
      .max(50, 'First name must be at most 50 characters'),
    admin_last_name: z
      .string()
      .min(2, 'Last name is required')
      .max(50, 'Last name must be at most 50 characters'),
    admin_email: z.string().email('Please enter a valid email address')
      .refine(isValidEmail, { message: 'Email must include a valid domain extension (e.g., .com, .org)' }),
  });

const passwordRequiredSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
    confirm_password: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

const passwordOptionalSchema = z.object({
  password: z.string().optional().or(z.literal('')),
  confirm_password: z.string().optional().or(z.literal('')),
});

/** Combined schema for the basic info form (base fields + optional password). Validation at runtime uses baseInfoSchema.and(passwordRequiredSchema) or baseInfoSchema.and(passwordOptionalSchema) depending on requiresPassword. */
const basicInfoSchema = baseInfoSchema.merge(passwordOptionalSchema);

export type BasicInfoFormData = z.infer<typeof basicInfoSchema>;

interface BasicInfoStepProps {
  data: ClinicOnboardingData;
  updateData: (updates: Partial<ClinicOnboardingData>) => void;
  onValidationChange?: (isValid: boolean) => void;
  inviteDetails: {
    email: string;
    clinicName: string;
    existingUser?: {
      firstName: string;
      lastName: string;
      hasPassword: boolean;
      isPractitioner?: boolean;
      practitionerId?: string | null;
      practitionerNpi?: string | null;
    } | null;
  } | null;
  requiresPassword: boolean;
  clinicArchetype?: 'guided' | 'direct' | 'hybrid';
}

export const BasicInfoStep = forwardRef<BasicInfoStepHandle, BasicInfoStepProps>(({
  data,
  updateData,
  onValidationChange,
  inviteDetails,
  requiresPassword,
  clinicArchetype,
}, ref) => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const isExistingPractitionerFromInvite = inviteDetails?.existingUser?.isPractitioner === true;
  const [states, setStates] = useState<State[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [npiValidation, setNpiValidation] = useState<{
    isValidating: boolean;
    exists: boolean;
  }>({
    isValidating: false,
    exists: false,
  });
  const [npiLookup, setNpiLookup] = useState<{
    isLoading: boolean;
    result: NpiLookupResult | null;
    hasAutofilled: boolean;
    error: string | null;
  }>({
    isLoading: false,
    result: null,
    hasAutofilled: false,
    error: null,
  });

  // Practitioner NPI validation state (seed isValidating from parent when existing practitioner was previously found)
  const [practitionerNpiValidation, setPractitionerNpiValidation] = useState<{
    isValidating: boolean;
    exists: boolean;
    practitioner?: { id: string; name: string; npi: string; email?: string };
  }>({
    isValidating: Boolean(data.add_as_practitioner && data.existing_practitioner_id),
    exists: false,
  });

  // Add as practitioner state
  const [addAsPractitioner, setAddAsPractitioner] = useState(data.add_as_practitioner);
  const [useClinicAddress, setUseClinicAddress] = useState(data.use_clinic_address_for_practitioner !== false);

  // Determine if admin card should be hidden because a practitioner account already exists
  // Either from the invite (backend detected existing practitioner user) or from NPI lookup
  const isExistingPractitioner = isExistingPractitionerFromInvite
    || (addAsPractitioner && practitionerNpiValidation.exists && !!practitionerNpiValidation.practitioner);

  const token = searchParams.get('token');

  // Credential options for practitioners
  const credentialOptions = [
    { value: 'MD', label: 'MD - Doctor of Medicine' },
    { value: 'DO', label: 'DO - Doctor of Osteopathic Medicine' },
    { value: 'NP', label: 'NP - Nurse Practitioner' },
    { value: 'PA', label: 'PA - Physician Assistant' },
    { value: 'DVM', label: 'DVM - Doctor of Veterinary Medicine' },
    { value: 'APRN', label: 'APRN - Advanced Practice Registered Nurse' },
    { value: 'CNM', label: 'CNM - Certified Nurse Midwife' },
    { value: 'DNP', label: 'DNP - Doctor of Nursing Practice' },
  ];
  // When existing practitioner is detected (via invite or NPI lookup), password is not needed
  const needsPassword = requiresPassword && !isExistingPractitioner;

  // Use a ref so the dynamic resolver always reads the latest value
  const needsPasswordRef = React.useRef(needsPassword);
  needsPasswordRef.current = needsPassword;

  const clinicArchetypeRef = React.useRef(clinicArchetype);
  clinicArchetypeRef.current = clinicArchetype;

  // Dynamic resolver that re-evaluates the schema on every validation
  const dynamicResolver = useCallback(
    async (values: any, context: any, options: any) => {
      const schema = baseInfoSchema.and(
        needsPasswordRef.current ? passwordRequiredSchema : passwordOptionalSchema,
      ).superRefine((data, ctx) => {
        if (clinicArchetypeRef.current === 'direct' && (!data.npi || !/^\d{10}$/.test(data.npi))) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'NPI is required for Direct clinics (10 digits)', path: ['npi'] });
        }
      });
      return zodResolver(schema)(values, context, options);
    },
    [],
  );

  const form = useForm<BasicInfoFormData>({
    resolver: dynamicResolver,
    defaultValues: {
      name: data.name || '',
      npi: data.npi || '',
      address_line_1: data.address_line_1 || '',
      address_line_2: data.address_line_2 || '',
      city: data.city || '',
      state: data.state || '',
      postal_code: data.postal_code || '',
      primary_phone: data.primary_phone || '',
      fax: data.fax || '',
      email: data.email || inviteDetails?.email || '',
      description: data.description || '',
      admin_first_name: data.admin_first_name || '',
      admin_last_name: data.admin_last_name || '',
      admin_email: data.admin_email || inviteDetails?.email || '',
      password: data.password || '',
      confirm_password: data.confirm_password || '',
    },
    mode: 'onChange',
  });

  // Expose triggerValidation to parent via ref (Bug 1: validation feedback on Next click)
  useImperativeHandle(ref, () => ({
    triggerValidation: async () => {
      const result = await form.trigger();
      return result && !npiValidation.exists && !npiValidation.isValidating;
    },
  }), [form, npiValidation.exists, npiValidation.isValidating]);

  // Bug 4 fix: Trigger validation on mount when returning to this step with pre-filled data.
  // react-hook-form in onChange mode only validates on field changes, not on initial values,
  // so isValid stays false when navigating back to a completed form.
  useEffect(() => {
    const hasValues = data.name && data.address_line_1 && data.city;
    if (hasValues) {
      const timer = setTimeout(() => form.trigger(), 150);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await stateService.getAllStates();
        if (response.data?.states) {
          setStates(response.data.states);
        }
      } catch (error) {
        console.error('Failed to fetch states:', error);
      }
    };
    fetchStates();
  }, []);

  // NPI Lookup and Autofill function
  const handleNpiLookup = useCallback(async () => {
    const npi = form.getValues('npi');
    
    if (!npi || !/^\d{10}$/.test(npi)) {
      toast({
        title: 'Invalid NPI',
        description: 'Please enter a valid 10-digit NPI number first',
        variant: 'destructive',
      });
      return;
    }

    if (!token) {
      toast({
        title: 'Error',
        description: 'Invalid session - please use the link from your email',
        variant: 'destructive',
      });
      return;
    }

    setNpiLookup(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await clinicOnboardingService.lookupNpi(npi, token);

      if (!result) {
        setNpiLookup({
          isLoading: false,
          result: null,
          hasAutofilled: false,
          error: 'NPI not found in the national registry',
        });
        toast({
          title: 'NPI Not Found',
          description: 'This NPI was not found in the NPPES registry. Please enter information manually.',
          variant: 'destructive',
        });
        return;
      }

      setNpiLookup({
        isLoading: false,
        result,
        hasAutofilled: true,
        error: null,
      });

      // Autofill form fields from NPI data
      if (result.entity_type === 'organization' && result.organization_name) {
        form.setValue('name', result.organization_name, { shouldValidate: true, shouldDirty: true });
      }

      if (result.address) {
        if (result.address.address_line_1) {
          form.setValue('address_line_1', result.address.address_line_1, { shouldValidate: true, shouldDirty: true });
        }
        if (result.address.address_line_2) {
          form.setValue('address_line_2', result.address.address_line_2, { shouldDirty: true });
        }
        if (result.address.city) {
          form.setValue('city', result.address.city, { shouldValidate: true, shouldDirty: true });
        }
        if (result.address.state) {
          // Find matching state code
          const matchedState = states.find(
            s => s.code === result.address?.state || s.name.toLowerCase() === result.address?.state?.toLowerCase()
          );
          if (matchedState) {
            form.setValue('state', matchedState.code, { shouldValidate: true, shouldDirty: true });
          }
        }
        if (result.address.postal_code) {
          form.setValue('postal_code', result.address.postal_code, { shouldValidate: true, shouldDirty: true });
        }
        if (result.address.phone) {
          form.setValue('primary_phone', result.address.phone, { shouldValidate: true, shouldDirty: true });
        }
        if (result.address.fax) {
          form.setValue('fax', result.address.fax, { shouldDirty: true });
        }
      }

      toast({
        title: 'NPI Data Found!',
        description: 'Form has been pre-filled with registry data. You can edit any fields as needed.',
        variant: 'success',
      });
    } catch (error) {
      console.error('NPI lookup error:', error);
      setNpiLookup({
        isLoading: false,
        result: null,
        hasAutofilled: false,
        error: 'Failed to look up NPI. Please try again or enter information manually.',
      });
      toast({
        title: 'Lookup Failed',
        description: 'Unable to fetch NPI data. Please enter information manually.',
        variant: 'destructive',
      });
    }
  }, [form, token, states, toast]);

  // Pre-fill from invite details
  useEffect(() => {
    if (inviteDetails?.email) {
      form.setValue('admin_email', inviteDetails.email);
    }

    if (inviteDetails?.existingUser) {
      const { firstName, lastName } = inviteDetails.existingUser;
      if (isExistingPractitionerFromInvite) {
        // For existing practitioners (from invite), always set the admin fields (they are hidden)
        form.setValue('admin_first_name', firstName || '', { shouldValidate: true });
        form.setValue('admin_last_name', lastName || '', { shouldValidate: true });
      } else {
        if (firstName && !form.getValues('admin_first_name')) {
          form.setValue('admin_first_name', firstName, { shouldValidate: true });
        }
        if (lastName && !form.getValues('admin_last_name')) {
          form.setValue('admin_last_name', lastName, { shouldValidate: true });
        }
      }
    }

    if (!requiresPassword) {
      form.setValue('password', '');
      form.setValue('confirm_password', '');
      form.clearErrors(['password', 'confirm_password']);
    }
  }, [inviteDetails, requiresPassword, form, isExistingPractitionerFromInvite]);

  // Auto-fill admin name when existing practitioner found via NPI lookup
  useEffect(() => {
    if (addAsPractitioner && practitionerNpiValidation.exists && practitionerNpiValidation.practitioner) {
      const fullName = practitionerNpiValidation.practitioner.name || '';
      const nameParts = fullName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      form.setValue('admin_first_name', firstName, { shouldValidate: true });
      form.setValue('admin_last_name', lastName, { shouldValidate: true });
    }
  }, [addAsPractitioner, practitionerNpiValidation.exists, practitionerNpiValidation.practitioner, form]);

  // Track the last validated NPI to avoid duplicate calls
  const lastValidatedNpiRef = React.useRef<string>('');
  const npiTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Track the last validated practitioner NPI
  const lastValidatedPractitionerNpiRef = React.useRef<string>('');
  const practitionerNpiTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Debounced practitioner NPI validation
  const validatePractitionerNpi = useCallback(
    async (npi: string) => {
      if (lastValidatedPractitionerNpiRef.current === npi) {
        return;
      }

      if (!npi || npi.length !== 10 || !/^\d{10}$/.test(npi)) {
        setPractitionerNpiValidation({ isValidating: false, exists: false });
        return;
      }

      lastValidatedPractitionerNpiRef.current = npi;
      setPractitionerNpiValidation(prev => ({ ...prev, isValidating: true }));

      try {
        // Use public route for onboarding (no auth required). Service returns unwrapped { exists, practitioner? }.
        const result = await practitionerService.checkPractitionerNpiExistsPublic(npi);
        setPractitionerNpiValidation({
          isValidating: false,
          exists: result.exists,
          practitioner: result.practitioner,
        });

        if (result.exists && result.practitioner) {
          updateData({ existing_practitioner_id: result.practitioner.id });
        } else {
          updateData({ existing_practitioner_id: undefined });
        }
      } catch (error) {
        console.error('Practitioner NPI validation error:', error);
        setPractitionerNpiValidation({ isValidating: false, exists: false });
        updateData({ existing_practitioner_id: undefined });
      }
    },
    [updateData]
  );

  // Debounced NPI validation
  const validateNpi = useCallback(
    async (npi: string) => {
      // Skip if already validated this NPI
      if (lastValidatedNpiRef.current === npi) {
        return;
      }

      if (!npi || npi.length !== 10 || !/^\d{10}$/.test(npi)) {
        setNpiValidation({ isValidating: false, exists: false });
        return;
      }

      lastValidatedNpiRef.current = npi;
      setNpiValidation(prev => ({ ...prev, isValidating: true }));

      try {
        const result = await clinicOnboardingService.checkNpiExists(npi);
        setNpiValidation({
          isValidating: false,
          exists: result.exists,
        });

        if (result.exists) {
          form.setError('npi', {
            type: 'manual',
            message: 'A clinic with this NPI already exists',
          });
        } else {
          form.clearErrors('npi');
        }
      } catch (error) {
        console.error('NPI validation error:', error);
        setNpiValidation({ isValidating: false, exists: false });
      }
    },
    [form]
  );

  // Watch NPI changes for validation with proper debouncing
  const npiValue = form.watch('npi');

  useEffect(() => {
    // Clear any existing timeout
    if (npiTimeoutRef.current) {
      clearTimeout(npiTimeoutRef.current);
    }

    // Only validate if NPI is 10 digits and different from last validated
    if (npiValue && npiValue.length === 10 && /^\d{10}$/.test(npiValue)) {
      if (lastValidatedNpiRef.current !== npiValue) {
        npiTimeoutRef.current = setTimeout(() => {
          validateNpi(npiValue);
        }, 500);
      }
    } else {
      // Reset validation state for incomplete NPI
      setNpiValidation({ isValidating: false, exists: false });
      lastValidatedNpiRef.current = '';
    }

    return () => {
      if (npiTimeoutRef.current) {
        clearTimeout(npiTimeoutRef.current);
      }
    };
  }, [npiValue, validateNpi]);

  // Re-validate practitioner NPI on mount when returning to this step (restores existing-practitioner state)
  useEffect(() => {
    if (addAsPractitioner && data.practitioner_npi && /^\d{10}$/.test(data.practitioner_npi)) {
      validatePractitionerNpi(data.practitioner_npi);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle practitioner NPI change
  const handlePractitionerNpiChange = useCallback((npi: string) => {
    updateData({ practitioner_npi: npi });

    // Clear any existing timeout
    if (practitionerNpiTimeoutRef.current) {
      clearTimeout(practitionerNpiTimeoutRef.current);
    }

    // Only validate if NPI is 10 digits
    if (npi && npi.length === 10 && /^\d{10}$/.test(npi)) {
      if (lastValidatedPractitionerNpiRef.current !== npi) {
        practitionerNpiTimeoutRef.current = setTimeout(() => {
          validatePractitionerNpi(npi);
        }, 500);
      }
    } else {
      setPractitionerNpiValidation({ isValidating: false, exists: false });
      lastValidatedPractitionerNpiRef.current = '';
      updateData({ existing_practitioner_id: undefined });
    }
  }, [updateData, validatePractitionerNpi]);

  // Handle add as practitioner checkbox change
  const handleAddAsPractitionerChange = useCallback((checked: boolean) => {
    setAddAsPractitioner(checked);
    updateData({ 
      add_as_practitioner: checked,
      use_clinic_address_for_practitioner: checked ? useClinicAddress : undefined,
    });
    
    if (!checked) {
      // Reset practitioner NPI validation when unchecked (keep existing_practitioner_id in parent for remount seeding)
      setPractitionerNpiValidation({ isValidating: false, exists: false });
      lastValidatedPractitionerNpiRef.current = '';
    } else {
      // Re-validate practitioner NPI when re-checked if there's already a valid NPI value
      const currentNpi = data.practitioner_npi;
      if (currentNpi && currentNpi.length === 10 && /^\d{10}$/.test(currentNpi)) {
        lastValidatedPractitionerNpiRef.current = '';
        validatePractitionerNpi(currentNpi);
      }
    }
  }, [updateData, useClinicAddress, data.practitioner_npi, validatePractitionerNpi]);

  // Handle use clinic address checkbox change
  const handleUseClinicAddressChange = useCallback((checked: boolean) => {
    setUseClinicAddress(checked);
    updateData({ use_clinic_address_for_practitioner: checked });
  }, [updateData]);

  // Sync form values to parent and update validation
  useEffect(() => {
    const subscription = form.watch(values => {
      updateData({
        ...values,
        admin_first_name: values.admin_first_name || '',
        admin_last_name: values.admin_last_name || '',
        admin_email: values.admin_email || inviteDetails?.email || '',
        confirm_password: values.confirm_password || '',
        password: requiresPassword ? values.password || '' : '',
      } as Partial<ClinicOnboardingData>);
    });
    return () => subscription.unsubscribe();
  }, [form, updateData, inviteDetails?.email, requiresPassword]);

  // Re-validate form when existing practitioner state changes (schema changes dynamically)
  const isExistingPractitionerPrevRef = React.useRef(isExistingPractitioner);
  useEffect(() => {
    // Only trigger re-validation when the value actually changes, not on initial mount
    if (isExistingPractitionerPrevRef.current !== isExistingPractitioner) {
      isExistingPractitionerPrevRef.current = isExistingPractitioner;
      if (isExistingPractitioner) {
        // Clear password errors since they're no longer required
        form.clearErrors(['password', 'confirm_password']);
        form.setValue('password', '', { shouldValidate: false });
        form.setValue('confirm_password', '', { shouldValidate: false });
      }
      // Re-validate only touched/dirty fields to avoid showing errors on untouched fields
      // form.trigger();
    }
  }, [isExistingPractitioner, form]);

  // Update validation status
  useEffect(() => {
    const isValid = form.formState.isValid && !npiValidation.exists && !npiValidation.isValidating;
    onValidationChange?.(isValid);
  }, [form.formState.isValid, npiValidation, onValidationChange]);

  // Handle Google Places address selection
  const handleAddressSelect = (addressData: any) => {
    console.log('handleAddressSelect called with:', addressData);

    if (!addressData) {
      console.error('No address data received');
      return;
    }

    // Set form values from Google Places autocomplete
    console.log('Setting address_line_1:', addressData.address_line_1);
    form.setValue('address_line_1', addressData.address_line_1 || '', {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    form.setValue('address_line_2', addressData.address_line_2 || '', {
      shouldValidate: false,
      shouldDirty: true,
    });

    console.log('Setting city:', addressData.city);
    form.setValue('city', addressData.city || '', {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    // Find the state code from the states list
    console.log('Setting state:', addressData.state);
    if (addressData.state) {
      const matchedState = states.find(
        s =>
          s.code === addressData.state || s.name.toLowerCase() === addressData.state.toLowerCase()
      );
      console.log('Matched state:', matchedState);
      if (matchedState) {
        form.setValue('state', matchedState.code, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      } else {
        // If no match found, try to use the state value directly
        form.setValue('state', addressData.state, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }

    // Only set postal_code if it was returned by Google Places
    console.log('Setting postal_code:', addressData.postal_code);
    if (addressData.postal_code) {
      form.setValue('postal_code', addressData.postal_code, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    console.log('Form values after setting:', form.getValues());
  };

  // Handle practitioner office address selection
  const handlePractitionerAddressSelect = useCallback(
    (addressData: any) => {
      if (!addressData) {
        return;
      }

      const matchedState = addressData.state
        ? states.find(
            s =>
              s.code === addressData.state || s.name.toLowerCase() === addressData.state.toLowerCase()
          )
        : undefined;

      updateData({
        practitioner_address_line_1: addressData.address_line_1 || '',
        practitioner_address_line_2: addressData.address_line_2 || '',
        practitioner_city: addressData.city || '',
        practitioner_state: matchedState?.code || '',
        practitioner_postal_code: addressData.postal_code || '',
      });
    },
    [states, updateData]
  );

  return (
    <Form {...form}>
      <form className='space-y-6'>
        {/* Clinical Setup — Smart questions that derive the archetype (on top so archetype drives later fields) */}
        <Card className='rounded-xl border border-gray-100 bg-slate-50 shadow-sm'>
          <CardContent className='space-y-5 p-4'>
            <h3 className='flex items-center gap-2 text-[0.8rem] font-semibold uppercase text-brand-teal tracking-wider'>
              <Stethoscope className='h-4 w-4' />
              Clinical Setup
            </h3>
            <p className='text-xs text-gray-500'>
              Help us understand how your clinic operates so we can configure the best experience for you.
            </p>

            {/* Question 1: Does your clinic have prescribers? */}
            <div className='space-y-3'>
              <p className='text-sm font-medium text-gray-900'>
                Does your clinic have licensed prescribers (MDs, DOs, NPs, PAs) who will write prescriptions for patients?
              </p>
              <RadioGroup
                value={
                  data.clinicArchetype === 'guided'
                    ? 'no'
                    : data.clinicArchetype
                      ? 'yes'
                      : ''
                }
                onValueChange={(value: string) => {
                  if (value === 'no') {
                    updateData({ clinicArchetype: 'guided' });
                  } else {
                    updateData({ clinicArchetype: data.clinicArchetype === 'guided' ? 'direct' : (data.clinicArchetype || 'direct') });
                  }
                }}
                className='grid gap-2'
              >
                <label
                  htmlFor='has-prescribers-yes'
                  className='flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 cursor-pointer hover:bg-gray-50 has-[:checked]:border-brand-teal has-[:checked]:bg-brand-teal/10'
                >
                  <RadioGroupItem value='yes' id='has-prescribers-yes' />
                  <div>
                    <p className='text-sm font-medium text-gray-900'>Yes — we have in-house prescribers</p>
                  </div>
                </label>
                <label
                  htmlFor='has-prescribers-no'
                  className='flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 cursor-pointer hover:bg-gray-50 has-[:checked]:border-brand-teal has-[:checked]:bg-brand-teal/10'
                >
                  <RadioGroupItem value='no' id='has-prescribers-no' />
                  <div>
                    <p className='text-sm font-medium text-gray-900'>No — we'd like NovaMed to provide clinical oversight</p>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Question 2: Only shown if Q1 = Yes — prescribing model */}
            {data.clinicArchetype && data.clinicArchetype !== 'guided' && (
              <div className='space-y-3 border-t border-gray-200 pt-4'>
                <p className='text-sm font-medium text-gray-900'>
                  Would you like to supplement your in-house prescribing with Nimbus Clinical Support for select programs?
                </p>
                <RadioGroup
                  value={data.clinicArchetype === 'hybrid' ? 'mix' : 'all-inhouse'}
                  onValueChange={(value: string) => {
                    updateData({ clinicArchetype: value === 'mix' ? 'hybrid' : 'direct' });
                  }}
                  className='grid gap-2'
                >
                  <label
                    htmlFor='prescribing-all-inhouse'
                    className='flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 cursor-pointer hover:bg-gray-50 has-[:checked]:border-brand-teal has-[:checked]:bg-brand-teal/10'
                  >
                    <RadioGroupItem value='all-inhouse' id='prescribing-all-inhouse' />
                    <div>
                      <p className='text-sm font-medium text-gray-900'>No — In-house only</p>
                      <p className='text-xs text-gray-500'>Our providers handle all prescribing</p>
                    </div>
                  </label>
                  <label
                    htmlFor='prescribing-mix'
                    className='flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 cursor-pointer hover:bg-gray-50 has-[:checked]:border-brand-teal has-[:checked]:bg-brand-teal/10'
                  >
                    <RadioGroupItem value='mix' id='prescribing-mix' />
                    <div>
                      <p className='text-sm font-medium text-gray-900'>Yes — Add Nimbus Clinical Support for select programs</p>
                      <p className='text-xs text-gray-500'>Some programs in-house, some with Nimbus Clinical Support</p>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            )}

            {/* Confirmation banner — shows what their selection means */}
            {data.clinicArchetype && (
              <div className={`rounded-xl p-4 text-sm ${
                data.clinicArchetype === 'guided'
                  ? 'bg-blue-50 border border-blue-200 text-blue-600'
                  : data.clinicArchetype === 'hybrid'
                    ? 'bg-amber-50 border border-amber-200 text-amber-600'
                    : 'bg-brand-teal/10 border border-brand-teal/20 text-brand-teal'
              }`}>
                <div className='flex items-start gap-2'>
                  <Info className='h-4 w-4 mt-0.5 shrink-0' />
                  <div>
                    {data.clinicArchetype === 'guided' && (
                      <p>
                        <strong>NovaMed Guided Care</strong> — NovaMed will pair your patients with our clinical team.
                        You'll earn a facilitation fee on each program enrollment.
                      </p>
                    )}
                    {data.clinicArchetype === 'direct' && (
                      <p>
                        <strong>Direct Prescribing</strong> — Your providers will prescribe directly through the platform.
                        You'll have full control over clinical decisions and medication-level pricing.
                      </p>
                    )}
                    {data.clinicArchetype === 'hybrid' && (
                      <p>
                        <strong>Flexible Model</strong> — You can toggle between in-house prescribing and Nimbus Clinical
                        Support on a per-program basis. Best of both worlds.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin Information */}
        {isExistingPractitioner ? (
          <Card className='bg-blue-50 border border-blue-200 rounded-xl'>
            <CardContent className='p-5'>
              <div className='flex items-start gap-4'>
                <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 shadow-md'>
                  <Link2 className='h-5 w-5 text-gray-900' />
                </div>
                <div className='space-y-1.5'>
                  <h3 className='text-sm font-semibold text-gray-900'>
                    Existing Practitioner Account Detected
                  </h3>
                  <p className='text-sm text-gray-600'>
                    An existing practitioner account was found
                    {(practitionerNpiValidation.practitioner?.email || inviteDetails?.existingUser?.isPractitioner) && (
                      <> for <strong className='text-gray-900'>
                        {practitionerNpiValidation.practitioner?.email || inviteDetails?.email}
                      </strong></>
                    )}
                    . The clinic admin account will be automatically linked to the practitioner profile.
                  </p>
                  <div className='flex items-center gap-2 pt-1'>
                    <span className='inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-medium text-blue-600'>
                      <Stethoscope className='h-3 w-3' />
                      {practitionerNpiValidation.practitioner?.name
                        || `${inviteDetails?.existingUser?.firstName || ''} ${inviteDetails?.existingUser?.lastName || ''}`.trim()
                        || 'Practitioner'}
                    </span>
                    {(practitionerNpiValidation.practitioner?.npi || inviteDetails?.existingUser?.practitionerNpi) && (
                      <span className='inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-gray-500'>
                        NPI: {practitionerNpiValidation.practitioner?.npi || inviteDetails?.existingUser?.practitionerNpi}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className='rounded-xl border border-gray-100 bg-slate-50 shadow-sm'>
            <CardContent className='space-y-4 p-4'>
              <h3 className='flex items-center gap-2 text-[0.8rem] font-semibold uppercase text-brand-teal tracking-wider'>
                <CheckCircle2 className='h-4 w-4' />
                Admin Account Information
              </h3>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <FormField
                  control={form.control}
                  name='admin_first_name'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='text-gray-700 font-medium text-sm'>
                        First Name <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder='Enter your first name' {...field} className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400' />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='admin_last_name'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='text-gray-700 font-medium text-sm'>
                        Last Name <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder='Enter your last name' {...field} className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400' />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Email (readonly) */}
              <FormField
                control={form.control}
                name='admin_email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='flex items-center gap-2 text-gray-700 font-medium text-sm'>
                      <Mail className='h-4 w-4' />
                      Email <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type='email'
                        placeholder='Your email address'
                        {...field}
                        readOnly
                        className='cursor-not-allowed bg-white border-gray-200 text-gray-900 rounded-xl'
                      />
                    </FormControl>
                    <p className='text-xs text-gray-500'>
                      This is the email from your invitation and will be used for login.
                    </p>
                    {!requiresPassword && (
                      <span className='mt-1 inline-flex items-center rounded-full border border-brand-teal/20 bg-brand-teal/10 px-2 py-0.5 text-[11px] font-medium text-brand-teal'>
                        Password already set
                      </span>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {requiresPassword ? (
                <>
                  {/* Password fields */}
                  <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    <FormField
                      control={form.control}
                      name='password'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className='flex items-center gap-2 text-gray-700 font-medium text-sm'>
                            <Lock className='h-4 w-4' />
                            Password <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                          </FormLabel>
                          <FormControl>
                            <div className='relative'>
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                placeholder='Create a password'
                                {...field}
                                className='pr-10 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                              />
                              <button
                                type='button'
                                onClick={() => setShowPassword(!showPassword)}
                                className='absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center p-1 rounded text-brand-teal hover:bg-brand-teal/10 hover:text-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal'
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                              >
                                {showPassword ? (
                                  <EyeOff className='h-4 w-4' />
                                ) : (
                                  <Eye className='h-4 w-4' />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='confirm_password'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className='flex items-center gap-2 text-gray-700 font-medium text-sm'>
                            <Lock className='h-4 w-4' />
                            Confirm Password <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                          </FormLabel>
                          <FormControl>
                            <div className='relative'>
                              <Input
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder='Confirm your password'
                                {...field}
                                className='pr-10 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                              />
                              <button
                                type='button'
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className='absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center p-1 rounded text-brand-teal hover:bg-brand-teal/10 hover:text-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal'
                                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                              >
                                {showConfirmPassword ? (
                                  <EyeOff className='h-4 w-4' />
                                ) : (
                                  <Eye className='h-4 w-4' />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <p className='text-xs text-gray-500'>
                    Password must be at least 8 characters and include uppercase, lowercase, number,
                    and special character.
                  </p>
                </>
              ) : (
                <div className='rounded-xl border border-brand-teal/20 bg-brand-teal/10 px-3 py-2 text-xs text-brand-teal'>
                  This account already has a password. You will use your existing credentials.
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add as Practitioner Section — hidden for guided clinics (no in-house prescribers) */}
        {clinicArchetype !== 'guided' && (
        <Card className='rounded-xl border border-gray-100 bg-slate-50 shadow-sm'>
          <CardContent className='space-y-4 p-4'>
            <div className='flex items-start space-x-3'>
              <Checkbox
                id='add_as_practitioner'
                checked={addAsPractitioner}
                onCheckedChange={handleAddAsPractitionerChange}
                className='mt-0.5'
              />
              <div className='flex-1'>
                <label
                  htmlFor='add_as_practitioner'
                  className='flex items-center gap-2 text-sm font-medium text-gray-900 cursor-pointer'
                >
                  <Stethoscope className='h-4 w-4 text-brand-teal' />
                  Also register me as a practitioner for this clinic
                </label>
                <p className='text-xs text-gray-500 mt-1'>
                  Check this box if you will also be prescribing medications or seeing patients at this clinic.
                </p>
              </div>
            </div>

            {/* Conditional Practitioner Fields */}
            {addAsPractitioner && (
              <div className='mt-4 space-y-4 border-t border-gray-200 pt-4'>
                <h4 className='flex items-center gap-2 text-[0.75rem] font-semibold uppercase text-gray-500'>
                  <Stethoscope className='h-3.5 w-3.5' />
                  Practitioner Information
                </h4>

                {/* NPI Number */}
                <div className='space-y-1'>
                  <label className='text-sm font-medium text-gray-900'>
                    Practitioner NPI <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <div className='relative'>
                    <Input
                      placeholder='Enter 10-digit practitioner NPI'
                      maxLength={10}
                      value={data.practitioner_npi || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        handlePractitionerNpiChange(value);
                      }}
                      className={`bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 ${practitionerNpiValidation.exists ? 'border-blue-600' : ''}`}
                    />
                    {practitionerNpiValidation.isValidating && (
                      <div className='absolute right-3 top-1/2 -translate-y-1/2 transform'>
                        <Loader2 className='h-4 w-4 animate-spin text-gray-400' />
                      </div>
                    )}
                    {practitionerNpiValidation.exists && !practitionerNpiValidation.isValidating && (
                      <div className='absolute right-3 top-1/2 -translate-y-1/2 transform'>
                        <Link2 className='h-4 w-4 text-blue-600' />
                      </div>
                    )}
                    {!practitionerNpiValidation.exists && !practitionerNpiValidation.isValidating && data.practitioner_npi?.length === 10 && (
                      <div className='absolute right-3 top-1/2 -translate-y-1/2 transform'>
                        <CheckCircle2 className='h-4 w-4 text-brand-teal' />
                      </div>
                    )}
                  </div>
                  {practitionerNpiValidation.exists && practitionerNpiValidation.practitioner && (
                    <div className='flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded-xl mt-2'>
                      <Info className='h-4 w-4 text-blue-600 mt-0.5 shrink-0' />
                      <div className='text-xs text-blue-600'>
                        <p className='font-medium'>Existing practitioner found</p>
                        <p>
                          <strong>{practitionerNpiValidation.practitioner.name}</strong> (NPI: {practitionerNpiValidation.practitioner.npi})
                          will be linked to your clinic.
                        </p>
                      </div>
                    </div>
                  )}
                  {!practitionerNpiValidation.exists && data.practitioner_npi?.length === 10 && (
                    <p className='text-xs text-brand-teal mt-1'>NPI is available - a new practitioner profile will be created.</p>
                  )}
                </div>

                {/* Only show additional fields if NPI doesn't exist (new practitioner) */}
                {!practitionerNpiValidation.exists && data.practitioner_npi?.length === 10 && (
                  <>
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                      {/* Credentials */}
                      <div className='space-y-1'>
                        <label className='text-sm font-medium text-gray-900'>
                          Credentials
                        </label>
                        <Select
                          value={data.practitioner_credentials || ''}
                          onValueChange={(value) => updateData({ practitioner_credentials: value })}
                        >
                          <SelectTrigger className='bg-white border-gray-200 text-gray-900'>
                            <SelectValue placeholder='Select credentials' />
                          </SelectTrigger>
                          <SelectContent>
                            {credentialOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* License Number */}
                      <div className='space-y-1'>
                        <label className='text-sm font-medium text-gray-900'>
                          License Number
                        </label>
                        <Input
                          placeholder='Enter license number'
                          value={data.practitioner_license_number || ''}
                          onChange={(e) => updateData({ practitioner_license_number: e.target.value })}
                          maxLength={30}
                          className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                        />
                      </div>
                    </div>

                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                      {/* DEA Number */}
                      <div className='space-y-1'>
                        <label className='text-sm font-medium text-gray-900'>
                          DEA Number
                        </label>
                        <Input
                          placeholder='Enter DEA number (optional)'
                          value={data.practitioner_dea_number || ''}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            updateData({ practitioner_dea_number: value });
                          }}
                          maxLength={20}
                          className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                        />
                        {data.practitioner_dea_number && getDEAValidationError(data.practitioner_dea_number) && (
                          <p className='text-xs text-red-500'>{getDEAValidationError(data.practitioner_dea_number)}</p>
                        )}
                      </div>

                    {/* Phone */}
                    <PhoneField
                      value={data.practitioner_phone || ''}
                      onChange={(val) => updateData({ practitioner_phone: val })}
                      label="Practitioner Phone"
                      placeholder="Enter phone number"
                      labelClassName="text-sm font-medium text-gray-900"
                      inputClassName="!bg-white !border-gray-200 !text-gray-900 [&::placeholder]:!text-gray-400 [&_.PhoneInputInput]:!bg-white [&_.PhoneInputInput]:!border-gray-200 [&_.PhoneInputInput]:!text-gray-900 [&_.PhoneInputInput]:placeholder:!text-gray-400"
                    />

                    {/* Fax */}
                    <PhoneField
                      value={data.practitioner_fax || ''}
                      onChange={(val) => updateData({ practitioner_fax: val })}
                      label="Practitioner Fax"
                      placeholder="Enter fax number"
                      labelClassName="text-sm font-medium text-gray-900"
                      inputClassName="!bg-white !border-gray-200 !text-gray-900 [&::placeholder]:!text-gray-400 [&_.PhoneInputInput]:!bg-white [&_.PhoneInputInput]:!border-gray-200 [&_.PhoneInputInput]:!text-gray-900 [&_.PhoneInputInput]:placeholder:!text-gray-400"
                    />
                    </div>

                    {/* Use Clinic Address Checkbox */}
                    <div className='flex items-center space-x-2 pt-2'>
                      <Checkbox
                        id='use_clinic_address'
                        checked={useClinicAddress}
                        onCheckedChange={handleUseClinicAddressChange}
                      />
                      <label
                        htmlFor='use_clinic_address'
                        className='text-sm font-medium text-gray-600 cursor-pointer'
                      >
                        Use clinic address for practitioner
                      </label>
                    </div>

                    {/* Custom Address Fields (shown when not using clinic address) */}
                    {!useClinicAddress && (
                      <div className='space-y-4 border-t border-gray-200 pt-4'>
                        <h5 className='text-xs font-medium uppercase text-gray-500'>Practitioner Office Address</h5>
                        <div className='relative z-50'>
                          <PlacesAutocomplete
                            onSelectPlace={handlePractitionerAddressSelect}
                            inputClassName='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                            suggestionsClassName='bg-white border-gray-200 [&_li]:text-gray-900 [&_li:hover]:bg-gray-100 [&_li_span.ml-1]:text-gray-500'
                          />
                        </div>
                        
                        <div className='space-y-1'>
                          <label className='text-sm font-medium text-gray-900'>
                            Address Line 1 <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                          </label>
                          <Input
                            placeholder='Enter street address'
                            value={data.practitioner_address_line_1 || ''}
                            onChange={(e) => updateData({ practitioner_address_line_1: e.target.value })}
                            maxLength={100}
                            className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                          />
                        </div>

                        <div className='space-y-1'>
                          <label className='text-sm font-medium text-gray-900'>
                            Address Line 2
                          </label>
                          <Input
                            placeholder='Suite, floor, etc. (optional)'
                            value={data.practitioner_address_line_2 || ''}
                            onChange={(e) => updateData({ practitioner_address_line_2: e.target.value })}
                            maxLength={100}
                            className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                          />
                        </div>

                        <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                          <div className='space-y-1'>
                            <label className='text-sm font-medium text-gray-900'>
                              City <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <Input
                              placeholder='Enter city'
                              value={data.practitioner_city || ''}
                              onChange={(e) => updateData({ practitioner_city: e.target.value })}
                              maxLength={50}
                              className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                            />
                          </div>

                          <div className='space-y-1'>
                            <label className='text-sm font-medium text-gray-900'>
                              State <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <Select
                              value={data.practitioner_state || ''}
                              onValueChange={(value) => updateData({ practitioner_state: value })}
                            >
                              <SelectTrigger className='bg-white border-gray-200 text-gray-900'>
                                <SelectValue placeholder='Select state' />
                              </SelectTrigger>
                              <SelectContent>
                                {states.map((state) => (
                                  <SelectItem key={state.id} value={state.code}>
                                    {state.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className='space-y-1'>
                            <label className='text-sm font-medium text-gray-900'>
                              Postal Code <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <Input
                              placeholder='Enter postal code'
                              value={data.practitioner_postal_code || ''}
                              className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                updateData({ practitioner_postal_code: value });
                              }}
                              maxLength={5}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Clinic Information */}
        <Card className='rounded-xl border border-gray-100 bg-slate-50 shadow-sm'>
          <CardContent className='space-y-4 p-4'>
            <h3 className='flex items-center gap-2 text-[0.8rem] font-semibold uppercase text-brand-teal tracking-wider'>
              <Building2 className='h-4 w-4' />
              Clinic Information
            </h3>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem>
<FormLabel className='text-gray-700 font-medium text-sm'>
                    Clinic Name <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder='Enter clinic name' maxLength={120} {...field} className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400' />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='npi'
                render={({ field }) => (
                  <FormItem>
<FormLabel className='text-gray-700 font-medium text-sm'>
                    NPI {clinicArchetype === 'direct' && <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>}
                    </FormLabel>
                    <FormControl>
                      <div className='flex gap-2'>
                        <div className='relative flex-1'>
                          <Input
                            placeholder={clinicArchetype === 'direct' ? 'Enter 10-digit NPI number' : 'NPI (optional)'}
                            maxLength={10}
                            {...field}
                            className={`bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 ${npiValidation.exists ? 'border-red-500' : ''}`}
                          />
                          {npiValidation.isValidating && (
                            <div className='absolute right-3 top-1/2 -translate-y-1/2 transform'>
                              <Loader2 className='h-4 w-4 animate-spin text-gray-400' />
                            </div>
                          )}
                          {npiValidation.exists && !npiValidation.isValidating && (
                            <div className='absolute right-3 top-1/2 -translate-y-1/2 transform'>
                              <AlertCircle className='h-4 w-4 text-red-500' />
                            </div>
                          )}
                          {npiLookup.hasAutofilled && !npiValidation.exists && !npiValidation.isValidating && (
                            <div className='absolute right-3 top-1/2 -translate-y-1/2 transform'>
                              <CheckCircle2 className='h-4 w-4 text-brand-teal' />
                            </div>
                          )}
                        </div>
                        <Button
                          type='button'
                          variant='outline'
                          size='default'
                          onClick={handleNpiLookup}
                          disabled={npiLookup.isLoading || !field.value || field.value.length !== 10}
                          className='shrink-0 gap-1.5 border-2 border-brand-teal bg-transparent text-brand-teal hover:bg-brand-teal/10 hover:border-brand-teal hover:text-brand-teal disabled:opacity-50 disabled:border-gray-200 disabled:text-gray-400'
                        >
                          {npiLookup.isLoading ? (
                            <Loader2 className='h-4 w-4 animate-spin' />
                          ) : (
                            <Search className='h-4 w-4' />
                          )}
                          <span className='hidden sm:inline'>Lookup</span>
                        </Button>
                      </div>
                    </FormControl>
                    {npiLookup.hasAutofilled && (
                      <p className='text-xs text-brand-teal flex items-center gap-1 mt-1'>
                        <Sparkles className='h-3 w-3' />
                        Auto-filled from NPPES registry
                      </p>
                    )}
                    {npiLookup.error && (
                      <p className='text-xs text-amber-600 mt-1'>{npiLookup.error}</p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

            </div>
          </CardContent>
        </Card>

        {/* Address Information */}
        <Card className='overflow-visible rounded-xl border border-gray-100 bg-slate-50 shadow-sm'>
          <CardContent className='space-y-4 overflow-visible p-4'>
            <h3 className='flex items-center gap-2 text-[0.8rem] font-semibold uppercase text-brand-teal tracking-wider'>
              <MapPin className='h-4 w-4' />
              Address
            </h3>

            <FormField
              control={form.control}
              name='address_line_1'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PlacesAutocomplete
                      onSelectPlace={handleAddressSelect}
                      value={field.value}
                      onManualChange={field.onChange}
                      label='Address Line 1'
                      required
                      labelClassName='text-gray-900 font-medium text-sm'
                      inputClassName='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                      suggestionsClassName='bg-white border-gray-200 [&_li]:text-gray-900 [&_li:hover]:bg-gray-100 [&_li_span.ml-1]:text-gray-500'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='address_line_2'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-gray-700 font-medium text-sm'>Address Line 2</FormLabel>
                  <FormControl>
                    <Input placeholder='Building, floor, or suite (optional)' {...field} className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
              <FormField
                control={form.control}
                name='city'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-gray-700 font-medium text-sm'>
                      City <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder='Enter city' {...field} className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400' />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='state'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-gray-700 font-medium text-sm'>
                      State <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className='bg-white border-gray-200 text-gray-900'>
                          <SelectValue placeholder='Choose state' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {states.map(state => (
                          <SelectItem key={state.id} value={state.code}>
                            {state.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='postal_code'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='text-gray-700 font-medium text-sm'>
                      Postal Code <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder='Enter postal code' maxLength={5} {...field} className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400' />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className='rounded-xl border border-gray-100 bg-slate-50 shadow-sm'>
          <CardContent className='space-y-4 p-4'>
            <h3 className='flex items-center gap-2 text-[0.8rem] font-semibold uppercase text-brand-teal tracking-wider'>
              <Phone className='h-4 w-4' />
              Contact Information
            </h3>

            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <PhoneInput
                form={form}
                name='primary_phone'
                placeholder='Enter phone number'
                label='Primary Phone'
                required
                labelClassName='text-gray-900 font-medium text-sm'
                inputClassName='!bg-white !border-gray-200 !text-gray-900 [&::placeholder]:!text-gray-400 [&_.PhoneInputInput]:!bg-white [&_.PhoneInputInput]:!border-gray-200 [&_.PhoneInputInput]:!text-gray-900 [&_.PhoneInputInput]:placeholder:!text-gray-400'
              />

              <PhoneInput
                form={form}
                name='fax'
                placeholder='Enter fax number'
                label='Fax'
                isFax={true}
                required={false}
                labelClassName='text-gray-900 font-medium text-sm'
                inputClassName='!bg-white !border-gray-200 !text-gray-900 [&::placeholder]:!text-gray-400 [&_.PhoneInputInput]:!bg-white [&_.PhoneInputInput]:!border-gray-200 [&_.PhoneInputInput]:!text-gray-900 [&_.PhoneInputInput]:placeholder:!text-gray-400'
              />
            </div>

            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-gray-700 font-medium text-sm'>
                    Email <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type='email' placeholder='Enter clinic email address' {...field} className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Description */}
        <Card className='rounded-xl border border-gray-100 bg-slate-50 shadow-sm'>
          <CardContent className='space-y-4 p-4'>
            <h3 className='flex items-center gap-2 text-[0.8rem] font-semibold uppercase text-brand-teal tracking-wider'>
              <FileText className='h-4 w-4' />
              Description
            </h3>

            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-gray-700 font-medium text-sm'>
                    Clinic Description <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='Enter a brief description of your clinic'
                      className='min-h-[100px] bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
      </form>
    </Form>
  );
});

BasicInfoStep.displayName = 'BasicInfoStep';

export default BasicInfoStep;
